import { FC, useEffect, useRef, useState } from 'react';

import { Nullable } from '@common/type/CommonTypes';
import { Box, Button, Divider, Rating, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import RatingStarIcon from '../ratingStarIcon';

import { NextImage } from '@components/atoms/image';
import { CommentStyle } from '@components/store/product/courseReview/style';
import { getAssetUrl } from '@helpers/utils';
import { CourseReviewEntity } from '@redux/course/type/courseDetailType';

const MAX_LINE_CLAMP = 3;

type CourseReviewItemProps = {
  review: CourseReviewEntity;
  isUnderline: boolean;
};

const CourseReviewItem: FC<CourseReviewItemProps> = ({ review, isUnderline }) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const commentRef = useRef<Nullable<HTMLDivElement>>(null);

  const [lineCount, setLineCount] = useState<number>(0);
  const [isShowAllComment, setIsShowAllComment] = useState<boolean>(false);

  useEffect(() => {
    if (commentRef.current) {
      const HEIGHT_OF_LINE = 24;
      setLineCount(Math.ceil(commentRef.current.offsetHeight / HEIGHT_OF_LINE));
    }
  }, [commentRef, review]);

  return (
    <Box>
      <Box display="flex" gap={2} py={2}>
        <Box width={40} height={40} borderRadius="100%" overflow="hidden">
          <NextImage
            src={review.userAvatar}
            alt={review.reviewerName}
            layout="responsive"
            width={40}
            height={40}
            errorImage={`${getAssetUrl('thumbnail-instructor.jpg')}`}
          />
        </Box>
        <Box minHeight={40}>
          <Typography variant="h5">{review.reviewerName}</Typography>
          <Rating
            value={review.rating}
            max={5}
            icon={<RatingStarIcon />}
            emptyIcon={<RatingStarIcon isEmpty />}
            size="small"
            readOnly
            sx={{ display: 'flex' }}
          />
        </Box>
      </Box>

      {review.comment ? (
        <Box pb={2}>
          <CommentStyle variant="body1lanna" isShowAll={isShowAllComment} lineClamp={String(MAX_LINE_CLAMP)}>
            <Typography ref={commentRef} component="span" variant="body1lanna">
              {review.comment}
            </Typography>
          </CommentStyle>
          {lineCount > MAX_LINE_CLAMP && (
            <Button
              onClick={() => setIsShowAllComment(!isShowAllComment)}
              variant="text"
              sx={{ color: theme.palette.primary.main, minWidth: 48, height: 22, p: 0 }}
              disableRipple
            >
              {isShowAllComment ? t('course_detail.course_review.see_less') : t('course_detail.course_review.see_more')}
            </Button>
          )}
        </Box>
      ) : null}

      {isUnderline ? <Divider /> : null}
    </Box>
  );
};

export default CourseReviewItem;
